<script>
  import { createEventDispatcher } from "svelte";
  import Select from "svelte-select/Select.svelte";
  import Button from "../../../Components/Common/Button.svelte";
  import DateInput from "../../../Components/Common/DateInput.svelte";
  import {
    DoctorMultiSearchSelect,
    PageLoading,
  } from "../../../Components/Common/index.svelte";
  import SvelteTable from "../../../Components/Common/SvelteTable.svelte";
  import { apiMethods } from "../../../Services/index.svelte";
  import { isMobile, prePopulateAccountOptions, user } from "../../../stores";
  import { SEARCH } from "../../../Utils/utilConstants.svelte";
  import { AustralianStates } from "../../../Utils/utilEnums.svelte";
  import {
    getTitle,
    renderTFIcons,
    showError,
  } from "../../../Utils/utilFunctions.svelte";
  import CalendarEventCategorySelector from "../../CalendarCategories/CalendarEventCategorySelector.svelte";

  export let isAvailabilityWizardOpen = false,
    cancelText = "Close",
    zIndex = 1000,
    showCrossButton = true,
    fromDate = "",
    toDate = "",
    selectedHospital,
    selectedDoctors = [],
    selectedProviderDetails,
    hospitalOptions = [],
    isLoadingProviders = false,
    filters = {
      state: "VIC",
    },
    selectedCategories,
    availableDoctors = [],
    isLoadingAvailableDoctors = false,
    haveSearched = false,
    filteredProviders = [];

  $: if (isAvailabilityWizardOpen) syncSearchDoctorFeatures();

  const syncSearchDoctorFeatures = async () => {
    setTimeout(() => {
      const doctorSearchInput = document.getElementById("doctorSearchInput");

      if (doctorSearchInput) {
        doctorSearchInput.style.borderWidth = "1px";
        doctorSearchInput.style.borderColor = "#818e9b66";
        doctorSearchInput.style.borderRadius = "6px";
        doctorSearchInput.style.padding = "0 15px";
        doctorSearchInput.style.height = "40px";

        doctorSearchInput.addEventListener("focusin", () => {
          doctorSearchInput.style.borderColor = "#0088f8";
        });

        doctorSearchInput.addEventListener("mouseenter", () => {
          doctorSearchInput.style.borderColor = "#818e9baa";
        });

        doctorSearchInput.addEventListener("blur", () => {
          doctorSearchInput.style.borderColor = "#818e9b66";
        });
      }
    }, 0);
  };

  const loadHospitals = async (searchText) => {
    const res = searchText
      ? await apiMethods.getMethod.getHospitals(0, 999999, {
          search: searchText,
        })
      : [];

    return _handleSearchHospitals(res.results?.length > 0 ? res.results : []);
  };

  const dispatch = createEventDispatcher();

  const closeModal = () => {
    isAvailabilityWizardOpen = false;
    selectedHospital = null;
    selectedDoctors = [];
    selectedProviderDetails = null;
    selectedCategories = [];
    haveSearched = false;
    filteredProviders = [];
    isLoadingAvailableDoctors = false;
    availableDoctors = [];
    hospitalOptions = [];
    fromDate = "";
    toDate = "";
  };

  const getFullName = (v) => {
    return `${getTitle(v.title)} ${v.firstName}${
      v.middleName ? " " + v.middleName : ""
    }${v.lastName ? " " + v.lastName : ""}`;
  };

  const renderSelectDoctorIcon = () => {
    return `<div class="flex flex-1 items-center justify-center gap-x-2">
        <button class="w-auto btn btn-secondary btn-sm rounded-xl text-white selectIcon" class="selectIcon">
          Select Doctor
        </button>
      </div>`;
  };

  const columns = [
    {
      key: "name",
      title: "Name",
      value: (v) => v.doctorName,
      headerClass: "text-left pl-4 text-[14px]",
      class: "text-left pl-4 text-[14px] align-bottom",
    },
    {
      key: "available",
      title: "On Call",
      headerClass: "text-left pl-4 text-[14px] cursor-default",
      class: "text-left pl-4 text-[14px]",
      renderValue: (v) => renderTFIcons(v.onCall),
    },
    {
      key: "buttons",
      title: "",
      headerClass: "text-center ml-3 cursor-default rounded-tr-xl",
      class: "px-3 cursor-default",
      renderValue: (v) => renderSelectDoctorIcon(),
    },
  ];

  const groupBy = (option) => option.groupName;

  const _handleSearchHospitals = (res = []) => {
    hospitalOptions = [];

    if (!res.length) return [];

    hospitalOptions = [...res];
    return [...res];
  };

  const handleDoctorSearch = async (text) => {
    if (!text || text.length < 2) {
      return [];
    }
    filters.name = text;
    await loadAllProviders();
  };

  const loadAllProviders = async () => {
    isLoadingProviders = true;

    filteredProviders = [];

    selectedProviderDetails = null;

    await apiMethods.getMethod
      .getAllProviders(0, 999999, filters)
      .then((res) => {
        if (!res.error) filteredProviders = _filterProviders(res.results);
        else filteredProviders = [];
      });

    isLoadingProviders = false;
  };

  const getOptionLabel = ({ title, firstName, lastName, speciality, state }) =>
    `${getTitle(title)} ${firstName} ${lastName} ${
      state ? ", " + state.toUpperCase() + " -" : ""
    } (${speciality})`;

  const clearCurrentProvider = () => {
    selectedProviderDetails = null;
  };

  // HELPER FUNCTIONS
  const _filterProviders = (arr = []) => {
    if (!arr.length) return [];

    return arr.map((doctor) => ({
      ...doctor,
      displayName: getOptionLabel(doctor),
    }));
  };

  const searchAvailableDoctor = () => {
    if (!fromDate || !toDate) return;

    haveSearched = true;
    isLoadingAvailableDoctors = true;
    const params = {
      "filter[fromdate]": fromDate,
      "filter[todate]": toDate,
    };
    if ($user.groupId || $user.groupId === 0)
      params["filter[groupid]"] = $user.groupId;

    if (selectedHospital && selectedHospital.id)
      params["filter[hospitalid]"] = selectedHospital.id;

    if (selectedProviderDetails && selectedProviderDetails.uniqueId)
      params["filter[allprovideruniqueid]"] = selectedProviderDetails.uniqueId;

    if (selectedDoctors && selectedDoctors.length)
      params["filter[doctorid]"] = selectedDoctors.join();

    if (selectedCategories && selectedCategories.length)
      params["filter[eventcategoryid]"] = selectedCategories.join();

    apiMethods.getMethod
      .getAccountAvailability(params)
      .then((res) => {
        if (res.error) throw res.error;
        if (res.results.length) {
          availableDoctors = res.results;
        } else {
          availableDoctors = [];
        }
        isLoadingAvailableDoctors = false;
      })
      .catch((err) => {
        showError(err);
        isLoadingAvailableDoctors = false;
      });
  };

  const handleClickCell = (
    { target: { className, classList } },
    rowData,
    key
  ) => {
    if (key === "buttons" && className.includes("selectIcon")) {
      selectDoctor(rowData);
    }
  };

  const selectDoctor = async (doctor) => {
    dispatch("availableDoctorSelected", {
      doctor: {
        id: doctor.doctorId,
        fullName: doctor.doctorName,
      },
      fromDate,
      toDate,
    });
    closeModal();
  };

  $: if (isAvailabilityWizardOpen) enableCloseDeleteWithESC();

  const enableCloseDeleteWithESC = () => {
    document.onkeydown = (event) => {
      if (event.key === "Escape") closeModal();
    };
  };
</script>

{#if isAvailabilityWizardOpen}
  <div
    class={isAvailabilityWizardOpen
      ? `modal modal-open z-[${zIndex}]`
      : "modal"}
  >
    <div class="modal-box no-transform overflow-visible p-0 flex flex-col">
      <div
        class="flex flex-1 items-start justify-between font-medium text-lg rounded-tl-xl rounded-tr-xl text-[#fff] flex-0 bg-[#8a3ed7] px-6 py-3"
      >
        <p>Doctors Availability</p>

        {#if showCrossButton}
          <button class="btn btn-circle btn-sm btn-ghost" on:click={closeModal}>
            <i class="fa fa-times" aria-hidden="true" />
          </button>
        {/if}
      </div>

      <div class="flex flex-col flex-1 gap-y-4 p-4">
        <div class="flex flex-row gap-4 flex-wrap">
          <div class="flex flex-col gap-y-2 w-80">
            <p>From*</p>

            <DateInput
              bind:value={fromDate}
              showTime
              wrapperClass="input input-sm px-2 input-bordered w-full h-[41px]"
              wrapperClassFocus="inputFocus"
              minDate={new Date()}
              maxDate={toDate}
            />
          </div>

          <div class="flex flex-col gap-y-2 w-80">
            <p>To*</p>

            <DateInput
              bind:value={toDate}
              showTime
              wrapperClass="input input-sm px-2 input-bordered w-full h-[41px]"
              wrapperClassFocus="inputFocus"
              minDate={fromDate}
            />
          </div>

          <div class="flex flex-col gap-y-2 w-80">
            <p>Hospital (optional)</p>

            <div class="h-[35px]">
              <Select
                bind:value={selectedHospital}
                id="hospitalSelect"
                class="flex flex-1 max-h-[35px] rounded-full"
                loadOptions={loadHospitals}
                items={hospitalOptions}
                {groupBy}
                itemId="id"
                label="name"
                noOptionsMessage="No hospital found"
                placeholder="Hospital"
              />
            </div>
          </div>

          <div class="flex flex-col gap-y-2 w-80">
            <p>Surgeon State (optional)</p>
            <select
              bind:value={filters.state}
              class="select-bordered select-sm select h-[41px] text-[#78848F] font-medium"
            >
              <option value={undefined} selected disabled>
                Select State
              </option>
              {#each AustralianStates as state}
                <option
                  value={state.key.toUpperCase()}
                  selected={state.key == "vic"}
                  disabled={isLoadingProviders}
                >
                  {state.name} - {state.key.toUpperCase()}
                </option>
              {/each}
            </select>
          </div>

          <div class="flex flex-col gap-y-2 w-80">
            <p>Surgeon (optional)</p>

            <Select
              bind:value={selectedProviderDetails}
              loadOptions={handleDoctorSearch}
              items={filteredProviders}
              itemId="uniqueId"
              label="displayName"
              debounceWait={SEARCH.DEBOUNCE_TIME}
              on:clear={clearCurrentProvider}
              noOptionsMessage="No Provider found"
              placeholder={isLoadingProviders ? "Loading..." : "Select Surgeon"}
              loading={isLoadingProviders}
            />
          </div>

          <div class="flex flex-col gap-y-2 w-80">
            <p>Specific Doctors (optional)</p>

            <DoctorMultiSearchSelect
              bind:searchResult={selectedDoctors}
              inputClass="flex flex-1 w-80 bg-[#fff]"
              searchTitle=""
              inputID="doctorSearchInput"
              zIndex="0"
            />
          </div>

          <div class="flex flex-col gap-y-2 w-80">
            <p>Calendar Categories (optional)</p>

            <CalendarEventCategorySelector
              multiSelect
              bind:value={selectedCategories}
              placeholder="Select Category"
              widthClass="flex-1"
              heightClass="max-h-[45px] h-full"
            />
          </div>
        </div>

        <div class="flex items-center justify-end gap-x-4 p-4">
          <div class="flex items-center gap-x-4">
            <Button
              manualClass="btn-error min-w-[100px]"
              clickFunc={closeModal}
            >
              {cancelText}
            </Button>

            <Button
              manualClass="min-w-[100px] btn-neutral"
              isDisabled={!fromDate || !toDate}
              clickFunc={searchAvailableDoctor}
              disabledTooltip="Time range required"
            >
              Search
            </Button>
          </div>
        </div>

        {#if isLoadingAvailableDoctors}
          <PageLoading />
        {:else if availableDoctors.length}
          <div class="relative w-full h-[50vh] mx-4">
            <div
              class={$isMobile
                ? "overflow-x-auto overflow-y-visible"
                : "overflow-y-auto absolute inset-0"}
            >
              <SvelteTable
                {columns}
                rows={availableDoctors}
                clickCellFunc={({ detail: { event, row, key } }) => {
                  handleClickCell(event, row, key);
                }}
              />
            </div>
          </div>
        {:else if haveSearched}
          <p class="text-error mx-4">No doctors available</p>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-box {
    max-width: 80%;
  }
</style>
